import axios from 'axios';
import config from '../config';

const API_BASE_URL = config.backend.host;

const openApi = axios.create({
    baseURL: `${API_BASE_URL}/api/bookmyop`,
    headers: {
        'Content-Type': 'application/json',
    }
});

// Specialties API
export const getSpecialties = async (searchQuery = '') => {
    try {
        const response = await openApi.get(`/specialties/${searchQuery ? `?search=${searchQuery}` : ''}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Service Provider Types API
export const getServiceProviderTypes = async (searchQuery = '') => {
    try {
        const response = await openApi.get(`/provider-types/${searchQuery ? `?search=${searchQuery}` : ''}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Doctors API
export const getDoctors = async (filters = {}) => {
    try {
        const queryParams = new URLSearchParams();
        
        // Add filters to query params
        if (filters.search) queryParams.append('search', filters.search);
        if (filters.specialty) queryParams.append('specialty', filters.specialty);
        if (filters.serviceProvider) queryParams.append('service_provider', filters.serviceProvider);
        if (filters.qualifications) queryParams.append('qualifications', filters.qualifications);
        
        // Add date filters
        if (filters.availableDate) queryParams.append('available_date', filters.availableDate);
        if (filters.availableDateStart) queryParams.append('available_date_start', filters.availableDateStart);
        if (filters.availableDateEnd) queryParams.append('available_date_end', filters.availableDateEnd);
        
        // Add pagination parameters
        if (filters.page) queryParams.append('page', filters.page);
        if (filters.pageSize) queryParams.append('page_size', filters.pageSize);
        
        // Add sorting parameters
        if (filters.ordering) queryParams.append('ordering', filters.ordering);
        
        const response = await openApi.get(`/doctors/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const getDoctor = async (doctorId) => {
    try {
        const response = await openApi.get(`/doctors/${doctorId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Service Providers API
export const getServiceProviders = async (filters = {}) => {
    try {
        const queryParams = new URLSearchParams();
        
        // Add filters to query params
        if (filters.search) queryParams.append('search', filters.search);
        if (filters.specialty) queryParams.append('specialties', filters.specialty);
        
        // Add date filters
        if (filters.availableDate) queryParams.append('available_date', filters.availableDate);
        if (filters.availableDateStart) queryParams.append('available_date_start', filters.availableDateStart);
        if (filters.availableDateEnd) queryParams.append('available_date_end', filters.availableDateEnd);
        
        // Add pagination parameters
        if (filters.page) queryParams.append('page', filters.page);
        if (filters.pageSize) queryParams.append('page_size', filters.pageSize);
        
        // Add sorting parameters
        if (filters.ordering) queryParams.append('ordering', filters.ordering);
        
        const response = await openApi.get(`/providers/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Get service providers for filter dropdown (simplified version)
export const getServiceProvidersForFilter = async () => {
    try {
        const response = await openApi.get('/providers/?page_size=100');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const getServiceProvider = async (providerId) => {
    try {
        const response = await openApi.get(`/providers/${providerId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Helper function to get doctors by service provider
export const getDoctorsByServiceProvider = async (providerId) => {
    try {
        const response = await openApi.get(`/doctors/?service_provider=${providerId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Helper function to get doctors by specialty
export const getDoctorsBySpecialty = async (specialtyId) => {
    try {
        const response = await openApi.get(`/doctors/?specialty=${specialtyId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Helper function to get service providers by specialty
export const getServiceProvidersBySpecialty = async (specialtyId) => {
    try {
        const response = await openApi.get(`/providers/?specialties=${specialtyId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Helper function to get service providers by type
// Note: This function is deprecated as provider_type filtering has been removed
export const getServiceProvidersByType = async (providerTypeId) => {
    try {
        const response = await openApi.get(`/providers/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Get detailed doctor information including time slots
export const getDoctorDetails = async (doctorId) => {
    if (!doctorId) {
        throw new Error('Doctor ID is required');
    }

    console.log('Making API call for doctor ID:', doctorId); // Debug log
    try {
        const response = await openApi.get(`/doctors/${doctorId}/details/`);
        console.log('API response:', response); // Debug log
        return response.data;
    } catch (error) {
        console.error('API error:', error.response || error); // Debug log
        throw error.response?.data || error.message;
    }
};

// Get available appointment slots for a specific time slot
export const getAvailableAppointmentSlots = async (doctorId, slotId, appointmentDate = null) => {
    if (!doctorId) {
        throw new Error('Doctor ID is required');
    }
    if (!slotId) {
        throw new Error('Slot ID is required');
    }

    try {
        let url = `/doctors/${doctorId}/slots/${slotId}/appointments/`;
        
        // Add date parameter if provided
        if (appointmentDate) {
            const dateString = typeof appointmentDate === 'string' ? appointmentDate : appointmentDate.toISOString().split('T')[0];
            url += `?appointment_date=${dateString}`;
        }
        
        console.log('Making appointment slots API call with:', { doctorId, slotId, appointmentDate, url }); // Debug log
        const response = await openApi.get(url);
        
        if (!response.data || !response.data.available_slots) {
            throw new Error('Invalid response format from server');
        }
        
        return response.data;
    } catch (error) {
        console.error('API error in getAvailableAppointmentSlots:', error.response || error);
        if (error.response?.status === 404) {
            throw new Error('Doctor or time slot not found');
        }
        throw error.response?.data || error.message;
    }
};

// Hospitals API - Enhanced with specific hospital filtering
export const getHospitals = async (filters = {}) => {
    try {
        const queryParams = new URLSearchParams();
        
        // Add filters to query params
        if (filters.search) queryParams.append('search', filters.search);
        if (filters.specialty) queryParams.append('specialties', filters.specialty);
        if (filters.bedCount) queryParams.append('bed_count', filters.bedCount);
        if (filters.location) queryParams.append('location', filters.location);
        
        // Add date filters
        if (filters.availableDate) queryParams.append('available_date', filters.availableDate);
        if (filters.availableDateStart) queryParams.append('available_date_start', filters.availableDateStart);
        if (filters.availableDateEnd) queryParams.append('available_date_end', filters.availableDateEnd);
        
        // Add pagination parameters
        if (filters.page) queryParams.append('page', filters.page);
        if (filters.pageSize) queryParams.append('page_size', filters.pageSize);
        
        // Add sorting parameters
        if (filters.ordering) queryParams.append('ordering', filters.ordering);
        
        const response = await openApi.get(`/providers/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Clinics API - Enhanced with specific clinic filtering
export const getClinics = async (filters = {}) => {
    try {
        const queryParams = new URLSearchParams();
        
        // Add filters to query params
        if (filters.search) queryParams.append('search', filters.search);
        if (filters.specialty) queryParams.append('specialties', filters.specialty);
        if (filters.clinicType) queryParams.append('clinic_type', filters.clinicType);
        if (filters.consultationFee) queryParams.append('consultation_fee', filters.consultationFee);
        if (filters.experienceYears) queryParams.append('experience_years', filters.experienceYears);
        if (filters.location) queryParams.append('location', filters.location);
        
        // Add pagination parameters
        if (filters.page) queryParams.append('page', filters.page);
        if (filters.pageSize) queryParams.append('page_size', filters.pageSize);
        
        // Add sorting parameters
        if (filters.ordering) queryParams.append('ordering', filters.ordering);
        
        const response = await openApi.get(`/providers/clinics/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Get hospital details with doctors
export const getHospitalDetails = async (hospitalId) => {
    if (!hospitalId) {
        throw new Error('Hospital ID is required');
    }

    try {
        const response = await openApi.get(`/providers/hospitals/${hospitalId}/details/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Get clinic details with doctors
export const getClinicDetails = async (clinicId) => {
    if (!clinicId) {
        throw new Error('Clinic ID is required');
    }

    try {
        const response = await openApi.get(`/providers/clinics/${clinicId}/details/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Get doctors by hospital
export const getDoctorsByHospital = async (hospitalId, filters = {}) => {
    try {
        const queryParams = new URLSearchParams();
        queryParams.append('service_provider', hospitalId);
        
        if (filters.specialty) queryParams.append('specialty', filters.specialty);
        if (filters.search) queryParams.append('search', filters.search);
        if (filters.page) queryParams.append('page', filters.page);
        if (filters.pageSize) queryParams.append('page_size', filters.pageSize);
        if (filters.ordering) queryParams.append('ordering', filters.ordering);
        
        const response = await openApi.get(`/doctors/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Get doctors by clinic
export const getDoctorsByClinic = async (clinicId, filters = {}) => {
    try {
        const queryParams = new URLSearchParams();
        queryParams.append('service_provider', clinicId);
        
        if (filters.specialty) queryParams.append('specialty', filters.specialty);
        if (filters.search) queryParams.append('search', filters.search);
        if (filters.page) queryParams.append('page', filters.page);
        if (filters.pageSize) queryParams.append('page_size', filters.pageSize);
        if (filters.ordering) queryParams.append('ordering', filters.ordering);
        
        const response = await openApi.get(`/doctors/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

